<template>
  <!-- ADMIN ONLY. Mounted by the shared map dialog's Overlays tab (only for an
       admin): keeps the "Public photos" layer on the page's map and owns the
       dialog a dot opens. The same arrangement as the hazard markers'
       HazardsMapHost — so the dialog exists on every page the overlay can be
       switched on, and nowhere else. Teleported to <body> because the tab lives
       inside a hidden modal. -->
  <Teleport to="body">
    <MapPhotoDetailDialog
      v-if="isPrimary"
      :open="open"
      :state="state"
      :views="views"
      :panel-style="panelStyle"
      @close="close"
    />
  </Teleport>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import MapPhotoDetailDialog from '../map_data_poi_dialog/MapPhotoDetailDialog.vue';
import type { PhotoSheetState } from '../map_data_poi_dialog/MapPhotoDetailDialog.vue';
import graph_client from '../../utils/graphql/graph_client';
import { reportHandledError } from '../../utils/bugsnag';
import { sharedPublicPhotosLayer } from '../../utils/publicPhotos/publicPhotosLayerController';
import { PublicPhotosLoader, buildPublicPhotoView } from '../../utils/publicPhotos/publicPhotoDetails';
import type { PublicPhotoView } from '../../utils/publicPhotos/publicPhotoDetails';
import { hazardPanelStyle } from '../../utils/trailHazards/hazardPanelPlacement';

// A page can render more than one map dialog (the live locator has two), and
// each one's Overlays tab mounts a host. They share the one layer; only the
// first owns the dialog, so a click never opens two.
let sheetOwner: symbol | null = null;

export default defineComponent({
  name: 'PublicPhotosMapHost',
  components: { MapPhotoDetailDialog },
  props: {
    /** Return true to ignore a click (e.g. while the planner places a waypoint). */
    ignoreClick: { type: Function as PropType<(event: any) => boolean>, default: null },
  },
  data() {
    return {
      isPrimary: false,
      open: false,
      state: 'loading' as PhotoSheetState,
      views: [] as PublicPhotoView[],
      panelStyle: null as Record<string, string> | null,
      justOpened: false,
    };
  },
  created() {
    (this as any).token = Symbol('public-photos-host');
    (this as any).loader = new PublicPhotosLoader(graph_client as any, reportHandledError);
    if (!sheetOwner) {
      sheetOwner = (this as any).token;
      this.isPrimary = true;
    }
  },
  mounted() {
    const layer = sharedPublicPhotosLayer();
    layer.acquire();
    if (!this.isPrimary) return;

    layer.setClickHandler((ids: string[]) => { this.openPhotos(ids); });
    layer.setClickFilter((event: any) => !!this.ignoreClick?.(event));
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.positionToMap);
    window.addEventListener('scroll', this.positionToMap, { passive: true });
  },
  beforeUnmount() {
    const layer = sharedPublicPhotosLayer();
    if (this.isPrimary) {
      layer.setClickHandler(null);
      layer.setClickFilter(null);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.positionToMap);
      window.removeEventListener('scroll', this.positionToMap);
      (this as any).loader.cancel();
      sheetOwner = null;
    }
    layer.release();
  },
  methods: {
    async openPhotos(ids: string[]) {
      this.open = true;
      this.state = 'loading';
      this.views = [];
      // The click that opened the panel is still on its way up to the document,
      // where it would otherwise close the panel straight away.
      this.justOpened = true;
      window.setTimeout(() => { this.justOpened = false; }, 0);
      this.$nextTick(this.positionToMap);

      const result = await (this as any).loader.load(ids);
      if (result.kind === 'stale') return;
      if (result.kind !== 'shown') {
        this.state = result.kind;
        return;
      }
      const t = (key: string, values?: Record<string, unknown>) => this.$t(key, values ?? {}) as string;
      this.views = result.photos.map((photo: any) => buildPublicPhotoView(photo, t, this.formatDate));
      this.state = 'shown';
    },

    formatDate(iso: string): string {
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return iso;
      try {
        return date.toLocaleString((window as any).hiikerLocale || undefined, { dateStyle: 'medium', timeStyle: 'short' });
      } catch {
        return date.toISOString();
      }
    },

    close() {
      this.open = false;
      this.views = [];
      (this as any).loader.cancel();
    },

    // Where the POI sheet and the hazard panel sit (hazardPanelPlacement is the
    // POI sheet's placement, extracted), so all three open in the same spot.
    positionToMap() {
      if (!this.open) return;
      const map = document.querySelector('.mapboxgl-map');
      const dock = document.querySelector('[data-map-dock="right"]');
      this.panelStyle = hazardPanelStyle({
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        mapRect: map ? map.getBoundingClientRect() : null,
        dockLeft: dock ? dock.getBoundingClientRect().left : null,
      });
    },

    // Clicking the map elsewhere closes the panel, as it does the POI sheet.
    onDocumentClick(event: MouseEvent) {
      if (!this.open || this.justOpened) return;
      const target = event.target as Element | null;
      if (target?.closest?.('[data-photo-sheet]') || target?.closest?.('.ds-lightbox')) return;
      this.close();
    },

    onKeydown(event: KeyboardEvent) {
      if (event.key === 'Escape' && this.open && !document.querySelector('.ds-lightbox')) this.close();
    },
  },
});
</script>
